import React, {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {signOut} from 'firebase/auth';
import moment from 'moment';

import {isHubAccountUid} from '@embertracking/common';
import {auth} from '-/firebase';
import {UserContext} from '-/contexts/User';
import {callFunction} from '-/data/utils';
import {expireSession} from '-/data/session';
import {HUB_SESSION_ENDED_MESSAGE} from '-/data/hubSession';

import AppBanner from './AppBanner';

import HubIcon from '@mui/icons-material/Hub';

// A hub session (#2307) is a regional hub user inside this department as an
// admin, on a one-hour token. Say so on every page — who, from which hub, and
// until when — and offer the early exit that revokes the session server-side.
// Not dismissible, for the same reason the operator and station banners aren't.
// Blue, not the operator's red: this is the district's normal work, not the
// platform reaching in.
//
// The session is the uid (`hub:<hub>:<uid>`, see common/accounts.js) plus its
// token claims; the expiry comes from the token, not from anything the
// department's data could change.
export default function HubSessionBanner() {
    const navigate = useNavigate();
    const {currentUser} = useContext(UserContext);
    const [session, setSession] = useState(null);

    const uid = currentUser?.uid;
    const isHubAccount = isHubAccountUid(uid);

    useEffect(() => {
        if (!isHubAccount || !auth.currentUser) {
            setSession(null);
            return undefined;
        }

        let cancelled = false;
        auth.currentUser.getIdTokenResult()
            .then(({claims}) => {
                if (cancelled) return;
                setSession(claims?.hubSession === true ? {expiresAt: claims.hsExp} : null);
            })
            .catch(() => {
                if (!cancelled) setSession(null);
            });

        return () => {
            cancelled = true;
        };
    }, [uid, isHubAccount]);

    // Sign out at the end time rather than waiting for the next api call or a
    // rules denial to notice: an idle tab would otherwise sit there looking
    // signed in.
    const expiresAt = session?.expiresAt;
    useEffect(() => {
        if (typeof expiresAt !== 'number') return undefined;
        const timer = setTimeout(() => {
            expireSession(HUB_SESSION_ENDED_MESSAGE);
        }, Math.max(0, expiresAt - Date.now()));
        return () => clearTimeout(timer);
    }, [expiresAt]);

    if (!session) return null;

    const onSignOut = async () => {
        try {
            await callFunction('hubSessionLogout');
        } catch (e) {
            // The session is ending either way; the api-side revoke is best
            // effort here and hsExp is the backstop.
        }
        await signOut(auth);
        navigate('/login');
    };

    const expiry = typeof expiresAt === 'number' ? moment(expiresAt) : null;

    return (
        <AppBanner
            tone="info"
            icon={<HubIcon />}
            action={{label: 'End Session', onClick: onSignOut, testId: 'hub-session-banner-signout'}}
            testId="hub-session-banner"
        >
            <strong>Signed in from the hub</strong> as {currentUser?.fullName || 'a hub user'}
            {expiry && <React.Fragment> &mdash; ends at {expiry.format('h:mm A')} ({expiry.fromNow()})</React.Fragment>}
            . Your changes are recorded under your name in this department&apos;s history.
        </AppBanner>
    );
}
